import { useState } from "react";
import { formatDateTime } from "../../../../Utils/formatDateTime";
import { useParams } from "react-router-dom";
import { useMemo } from "react";
import DataTable from "react-data-table-component";
import { Button, Spinner, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import {
    getOperatorCompanyByIdService,
    updateOperatorStaffStatusService,
    getOperatorEoisService,
    getOperatorUsageService,
    getOperatorSearchesService,
    getPlatformStatesService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

// NOTE: adjust the relative import paths to match this file's real location.

const customStyles = {
    head: {
        style: { fontWeight: "800", color: "#0000ff", fontSize: "14px" },
    },
};

const dash = (val: any) => (val === null || val === undefined || val === "" ? "-" : val);
// N21: API datetimes are UTC without a marker - formatDateTime reads them as UTC
// and renders Australian Eastern time.
const formatDate = (val: string | null) => formatDateTime(val);


const STRATEGY_LABELS: Record<string, string> = {
    Cashflow: "Income now",
    Balanced: "Balanced",
    Growth: "Long-term growth",
};

const displayStrategy = (value: any) => {
    if (!value) return "-";
    return STRATEGY_LABELS[value] || value;
};

const formatSearchParams = (params: any, stateIdToName: Record<string, string>) => {
    if (!params) return "-";
    const budget = `Up to $${(params.budgetMax ?? 0).toLocaleString()}`;
    const type = params.propertyType ? params.propertyType : "All";
    const states = params.stateIds
        ? params.stateIds
              .split(",")
              .map((id: string) => stateIdToName[id.trim()] || id.trim())
              .join(", ")
        : "All";
    const bedrooms =
        params.bedroomsMin || params.bedroomsMax
            ? `${params.bedroomsMin ?? "any"}-${params.bedroomsMax ?? "any"} beds`
            : "All";
    const contractType = params.contractType ? params.contractType : "All";
    const propertyReady = params.propertyReady ? params.propertyReady : "All";

    return [
        `Budget: ${budget}`,
        `Type: ${type}`,
        `States: ${states}`,
        `Bedrooms: ${bedrooms}`,
        `Contract: ${contractType}`,
        `PropertyReady: ${propertyReady}`,
    ].join(" \u00b7 ");
};
const OperatorMemberDetail = () => {
    const { companyId } = useParams<{ companyId: string }>();

    const [staffModal, setStaffModal] = useState(false);
    const [pendingStaff, setPendingStaff] = useState<any>(null);
    const [staffConflictMsg, setStaffConflictMsg] = useState<string | null>(null);

    const [usageFrom, setUsageFrom] = useState("");
    const [usageTo, setUsageTo] = useState("");

    const [searchPage, setSearchPage] = useState(1);
    const searchPageSize = 20;

    const {
        data: companyData,
        isLoading,
        isError,
        refetch,
    } = useGetRequestQuery<any>(["getOperatorCompanyById", companyId], () => getOperatorCompanyByIdService(companyId!), {
        enabled: !!companyId,
    });

    const company = !isError ? companyData?.data : null;
  

const { data: statesData } = useGetRequestQuery<any>(
    ["getPlatformStates"],
    () => getPlatformStatesService(),
    {}
);

// stateId -> stateName lookup, built once the states list loads
const stateIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    (statesData?.data || []).forEach((s: any) => {
        map[String(s.stateId)] = s.stateName;
    });
    return map;
}, [statesData]);
    const { data: eoiData, isLoading: eoiLoading } = useGetRequestQuery<any>(
        ["getOperatorCompanyEois", companyId],
        () => getOperatorEoisService({ companyId }),
        { enabled: !!companyId }
    );
    // Newest EOI first, always - sorted here explicitly instead of trusting
    // the API's order, since that wasn't reliably newest-first.
    const eois = [...(eoiData?.data || [])].sort(
        (a: any, b: any) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    );

    const {
        data: usageData,
        isLoading: usageLoading,
        refetch: refetchUsage,
    } = useGetRequestQuery<any>(
        ["getOperatorUsage", companyId, usageFrom, usageTo],
        () =>
            getOperatorUsageService(companyId!, {
                ...(usageFrom ? { fromDate: usageFrom } : {}),
                ...(usageTo ? { toDate: usageTo } : {}),
            }),
        { enabled: !!companyId }
    );
    const usage = usageData?.data;

    const { data: searchesData, isLoading: searchesLoading } = useGetRequestQuery<any>(
        ["getOperatorCompanySearches", companyId, searchPage],
        () =>
            getOperatorSearchesService({
                companyId,
                pageNumber: searchPage,
                pageSize: searchPageSize,
            }),
        { enabled: !!companyId }
    );
    // searches[] is already newest-first from the API - never re-sort it here.
    const searches = searchesData?.data?.searches || [];
    const totalSearches = searchesData?.data?.totalSearches ?? 0;

    const { mutate: toggleStaffStatus, isPending: isTogglingStaff } = useMutationRequestQuery(
        "updateOperatorStaffStatus",
        updateOperatorStaffStatusService,
        {
            onSuccess: (res: any) => {
                toast.success(res?.data?.message || "Login status updated.");
                setStaffModal(false);
                setPendingStaff(null);
                setStaffConflictMsg(null);
                refetch();
            },
            onError: (err: any) => {
                const status = err?.response?.status;
                if (status === 409) {
                    // Normal - the API refuses to leave an active company with nobody who can log in
                    setStaffConflictMsg(
                        err?.response?.data?.message ||
                            "This is the only active login for the company. Disable the whole member account instead."
                    );
                } else if (status === 404) {
                    toast.error("Staff member not found. Refreshing the list.");
                    setStaffModal(false);
                    setPendingStaff(null);
                    refetch();
                } else {
                    toast.error(err?.response?.data?.message || "Failed to update login status.");
                    setStaffModal(false);
                    setPendingStaff(null);
                }
            },
        }
    );

    const openStaffModal = (staff: any) => {
        setPendingStaff(staff);
        setStaffConflictMsg(null);
        setStaffModal(true);
    };

    const confirmStaffToggle = () => {
        if (!pendingStaff) return;
        toggleStaffStatus({ staffId: pendingStaff.staffId, isActive: !pendingStaff.isActive });
    };

    const staffColumns = [
        {
            name: "Name",
            cell: (row: any) => (
                <span>
                    {row.firstName} {row.surname}
                    {row.isOwner && (
                        <Badge color="info" className="ms-2">
                            Owner
                        </Badge>
                    )}
                </span>
            ),
            width: "12rem",
        },
        { name: "Mobile", selector: (row: any) => dash(row.mobile), width: "9rem" },
        { name: "Email", selector: (row: any) => dash(row.email), width: "14rem" },
        {
            name: "Verified",
            cell: (row: any) => (row.isEmailVerified ? "✓" : "✗"),
            width: "7rem",
        },
        {
            name: "Password set",
            cell: (row: any) => (row.hasSetPassword ? "✓" : "Invited, not activated"),
            width: "10rem",
        },
        {
            name: "Last login",
            cell: (row: any) => formatDate(row.lastLoginOn),
            width: "11rem",
        },
        {
            name: "Status",
            width: "12rem",
            cell: (row: any) =>
                company.isActive ? (
                    <div className="d-flex align-items-center gap-2">
                        <Badge color={row.isActive ? "success" : "secondary"}>
                            {row.isActive ? "Active" : "Disabled"}
                        </Badge>
                        <Button
                            size="sm"
                            outline
                            color={row.isActive ? "danger" : "success"}
                            onClick={() => openStaffModal(row)}
                        >
                            {row.isActive ? "Disable" : "Enable"}
                        </Button>
                    </div>
                ) : (
                    <Badge color="secondary">Disabled</Badge>
                ),
        },
    ];

  const searchColumns = [
    { name: "Date", selector: (row: any) => formatDate(row.createdOn), width: "11rem" },
    {
        name: "Staff Member",
        cell: (row: any) => <span title={row.staffEmail}>{dash(row.staffMember)}</span>,
        width: "10rem",
    },
    { name: "Strategy", selector: (row: any) => displayStrategy(row.strategy), width: "8rem" },
    {
        name: "Filters",
        cell: (row: any) => <span>{formatSearchParams(row.parameters, stateIdToName)}</span>,
        grow: 2,
    },
    { name: "Results", selector: (row: any) => row.resultCount, width: "7rem" },
];
    const searchTotalPages = Math.max(1, Math.ceil(totalSearches / searchPageSize));

   const eoiColumns = [
    { name: "Date", selector: (row: any) => formatDate(row.createdOn), width: "12rem" },
    { name: "Staff Member", selector: (row: any) => dash(row.staffMember), width: "12rem" },
    {
        name: "Property Address",
        cell: (row: any) => (
            <span title={`Property ID: ${row.propertyId}`}>{dash(row.propertyAddress)}</span>
        ),
        width: "18rem",
    },
    {
        name: "Note",
        cell: (row: any) => <span title={row.noteToFintrack}>{dash(row.noteToFintrack)}</span>,
        grow: 1,
    },
];

    if (isLoading) {
        return (
            <div className="mt-4 mb-2" style={{ textAlign: "center" }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (isError || !company) {
        return (
            <div className="mt-4 mb-2" style={{ textAlign: "center" }}>
                <h6>Member not found.</h6>
            </div>
        );
    }

    return (
        <>
            <div className="px-4 pt-4">
                <div
                    className="p-3 mb-3"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <h3 className="f-w-900 heading-color mb-0">{company.businessName}</h3>
                        <Badge color={company.isActive ? "success" : "secondary"} style={{ fontSize: "14px" }}>
                            {company.isActive ? "Active" : "Disabled"}
                        </Badge>
                    </div>

                    <div className="row">
                        {/* Profile panel - all read-only, Operator cannot edit */}
                        <div className="col-md-4">
                            <h6 className="f-w-700 mb-3">Profile</h6>
                           {company.logoUrl && (
        <div
            style={{
                width: "100%",
                maxWidth: 220,
                height: 140,
                border: "1px solid #eee",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 12,
                marginBottom: 16,
                backgroundColor: "#fafafa",
            }}
        >
            <img
                src={company.logoUrl}
                alt={`${company.businessName} logo`}
                style={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                }}
            />
        </div>
    )}
                            <p>
                                <strong>ACN:</strong> {dash(company.acn)}
                            </p>
                            <p>
                                <strong>Contact:</strong> {dash(company.contactName)}
                            </p>
                            <p>
                                <strong>Mobile:</strong> {dash(company.contactMobile)}
                            </p>
                            <p>
                                <strong>Email:</strong> {dash(company.contactEmail)}
                            </p>
                            {company.bookingLink && (
                                <p>
                                    <strong>Booking link:</strong>{" "}
                                    <a href={company.bookingLink} target="_blank" rel="noreferrer">
                                        {company.bookingLink}
                                    </a>
                                </p>
                            )}
                            <p>
                                <strong>Brand colours:</strong>
                                <br />
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 16,
                                        height: 16,
                                        backgroundColor: company.brandColorPrimary || "#fff",
                                        border: "1px solid #ccc",
                                        marginRight: 6,
                                        verticalAlign: "middle",
                                    }}
                                />
                                {dash(company.brandColorPrimary)}{" "}
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 16,
                                        height: 16,
                                        backgroundColor: company.brandColorSecondary || "#fff",
                                        border: "1px solid #ccc",
                                        marginLeft: 12,
                                        marginRight: 6,
                                        verticalAlign: "middle",
                                    }}
                                />
                                {dash(company.brandColorSecondary)}
                            </p>
                            <p>
                                <strong>Created:</strong> {formatDate(company.createdOn)}
                            </p>
                            <p>
                                <strong>Updated:</strong> {formatDate(company.updatedOn)}
                            </p>
                            <p>
                                <strong>Owner:</strong> {dash(company.ownerName)} ({dash(company.ownerEmail)})
                            </p>
                        </div>

                        {/* Stat tiles + usage date range */}
                        <div className="col-md-8">
                            <h6 className="f-w-700 mb-3">Usage</h6>
                            <div className="d-flex gap-4 flex-wrap mb-3">
                                <StatTile label="Staff" value={company.staffCount} />
                                <StatTile label="Searches" value={usage ? usage.searchCount : company.searchCount} />
                                <StatTile label="Reports" value={usage ? usage.reportCount : company.reportCount} />
                                <StatTile label="EOIs" value={usage ? usage.eoiCount : company.eoiCount} />
                                <StatTile label="Last Activity" value={formatDate(company.lastActivityOn)} />
                            </div>

                            <div className="d-flex align-items-end gap-2 mb-2">
                                <div>
                                    <label className="d-block">From</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={usageFrom}
                                        onChange={(e) => setUsageFrom(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="d-block">To</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={usageTo}
                                        onChange={(e) => setUsageTo(e.target.value)}
                                    />
                                </div>
                                <Button color="primary" onClick={() => refetchUsage()} disabled={usageLoading}>
                                    {usageLoading ? "Loading..." : "Apply"}
                                </Button>
                                {(usageFrom || usageTo) && (
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={() => {
                                            setUsageFrom("");
                                            setUsageTo("");
                                        }}
                                    >
                                        All-time
                                    </Button>
                                )}
                            </div>
                            <small className="text-muted">
                                Leave dates blank for all-time counts. The tiles above show the selected range once
                                applied.
                            </small>
                        </div>
                    </div>
                </div>

                {/* Staff table */}
                <div
                    className="p-3 mb-3"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <h6 className="f-w-700 mb-3">Staff</h6>
                    <DataTable columns={staffColumns} data={company.staff || []} customStyles={customStyles} responsive />
                </div>

                {/* EOI table */}
                <div
                    className="p-3 mb-4"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}
                >
                    <h6 className="f-w-700 mb-3">EOIs</h6>
                    {eoiLoading ? (
                        <Spinner color="primary" />
                    ) : eois.length === 0 ? (
                        <p className="text-muted mb-0">This company hasn't submitted any Expressions of Interest yet.</p>
                    ) : (
                        <DataTable columns={eoiColumns} data={eois} customStyles={customStyles} responsive />
                    )}
                </div>

                {/* Searches table - individual property searches this company has made.
                    No client names come back from this endpoint (by design), and the
                    API already returns newest-first - never re-sorted here. */}
                <div
                    className="p-3 mb-4"
                    style={{ borderRadius: "8px", boxShadow: "0 4px 8px rgba(45, 44, 44, 0.1)", backgroundColor: "#fff" }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="f-w-700 mb-0">Searches</h6>
                        {!searchesLoading && <span className="text-muted">Total: {totalSearches}</span>}
                    </div>
                    {searchesLoading ? (
                        <Spinner color="primary" />
                    ) : searches.length === 0 ? (
                        <p className="text-muted mb-0">No property searches have been run by this company's staff yet.</p>
                    ) : (
                        <>
                            <DataTable columns={searchColumns} data={searches} customStyles={customStyles} responsive />
                            {searchTotalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        outline
                                        disabled={searchPage <= 1}
                                        onClick={() => setSearchPage((p) => Math.max(1, p - 1))}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-muted">
                                        Page {searchPage} of {searchTotalPages}
                                    </span>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        outline
                                        disabled={searchPage >= searchTotalPages}
                                        onClick={() => setSearchPage((p) => Math.min(searchTotalPages, p + 1))}
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Enable / Disable staff login confirmation */}
            <Modal isOpen={staffModal} toggle={() => setStaffModal(!staffModal)} centered>
                <ModalHeader toggle={() => setStaffModal(!staffModal)}>
                    {pendingStaff?.isActive ? "Disable login" : "Enable login"}
                </ModalHeader>
                <ModalBody>
                    {staffConflictMsg ? (
                        <div className="alert alert-warning mb-0">{staffConflictMsg}</div>
                    ) : (
                        <>
                            {pendingStaff?.isActive ? "Disable" : "Enable"} the login for{" "}
                            <strong>
                                {pendingStaff?.firstName} {pendingStaff?.surname}
                            </strong>
                            ?
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setStaffModal(false)}>
                        Close
                    </Button>
                    {!staffConflictMsg && (
                        <Button
                            color={pendingStaff?.isActive ? "danger" : "success"}
                            onClick={confirmStaffToggle}
                            disabled={isTogglingStaff}
                        >
                            {isTogglingStaff ? "Saving..." : pendingStaff?.isActive ? "Disable" : "Enable"}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </>
    );
};

const StatTile = ({ label, value }: { label: string; value: any }) => (
    <div
        style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: "10px 16px",
            minWidth: 100,
            textAlign: "center",
        }}
    >
        <div style={{ fontSize: 20, fontWeight: 800 }}>{value ?? "-"}</div>
        <div style={{ fontSize: 12, color: "#888" }}>{label}</div>
    </div>
);

export default OperatorMemberDetail;